// ============================================================
// AGENT GUIDE SERVICE
// ============================================================
// CRUD for reusable `agent_presenters` and per-scene
// `scene_guides`, plus the wrapper around the
// `tours-generate-narration` edge function.
// ============================================================

import { supabase, isConfigured } from './supabase';
import type {
    AgentPresenter,
    AgentPresenterMode,
    AgentVoiceProvider,
    AvatarSettings,
    Hotspot,
    HotspotVoiceSettings,
    HotspotVoiceTrack,
    OrbSettings,
    OrbStyle,
    RecordedGuideDisplay,
    RecordedGuideSettings,
    RecordedMedia,
    RecordedMediaType,
    ResolvedSceneGuide,
    ResolvedTourGuide,
    SceneGuide,
    SceneGuideOverrides,
    TourAgentSettings,
    TourGuide,
    TourSettings,
} from '../types';

// ---- ElevenLabs voice catalogue (curated subset) ----
// The catalogue, language list and grouping logic now live in the shared
// @rehub/media-localisation package (also consumed by Rehub Record). They
// are re-exported here under their original names so nothing else in the
// tours app changes.

import {
    MEDIA_VOICE_LIBRARY,
    DEFAULT_VOICE_ID as SHARED_DEFAULT_VOICE_ID,
    MEDIA_LANGUAGES,
    DEFAULT_LANGUAGE_CODE as SHARED_DEFAULT_LANGUAGE_CODE,
    languageFlagFor as sharedLanguageFlagFor,
    mergeVoiceCatalogues as sharedMergeVoiceCatalogues,
    groupVoices as sharedGroupVoices,
    deriveVoiceGroup,
    titleCase,
    type MediaVoiceOption,
    type MediaLanguageOption,
} from '@rehub/media-localisation';

export type AgentVoiceOption = MediaVoiceOption;

// Additional English accents (Irish, Scottish, Indian, …) and native
// language voices (Spanish, Italian, …) are loaded live from the Voice
// Library via tours-list-voices and merged in by useAgentVoices.
export const AGENT_VOICE_LIBRARY: AgentVoiceOption[] = MEDIA_VOICE_LIBRARY;

export const DEFAULT_VOICE_ID = SHARED_DEFAULT_VOICE_ID;

/** The hardcoded fallback catalogue, grouped. */
export const getGroupedVoices = () => groupVoices(AGENT_VOICE_LIBRARY);

// ---- Dynamic voice loading (real, current account voices) ----

interface RawAccountVoice {
    id: string;
    name: string;
    accent: string | null;
    gender: string | null;
    language: string | null;
    languages: string[];
    category: string | null;
    public_owner_id?: string | null;
    /** True when sourced from the Voice Library (may need add-on-first-use). */
    from_library?: boolean;
}

/**
 * Merge curated defaults with live account/library voices — shared
 * implementation from @rehub/media-localisation (unchanged behaviour).
 */
export const mergeVoiceCatalogues = sharedMergeVoiceCatalogues;

/** Group any voice list by its `group` label, preserving preferred order. */
export const groupVoices = sharedGroupVoices;

/**
 * Fetch the account's real ElevenLabs voices via `tours-list-voices`
 * (includes curated Voice Library accents / languages), normalised into
 * AgentVoiceOption[]. Returns [] on any failure so callers can fall back
 * to AGENT_VOICE_LIBRARY.
 */
export const fetchAccountVoices = async (): Promise<AgentVoiceOption[]> => {
    if (!isConfigured) return [];
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session?.access_token) return [];

    const { data, error } = await supabase.functions.invoke('tours-list-voices', { body: {} });
    if (error || !data || !Array.isArray((data as { voices?: unknown }).voices)) return [];

    const list = (data as { voices: RawAccountVoice[] }).voices;
    return list
        .filter((v) => v.id && v.name)
        .map((v) => {
            const descr = [v.accent ? titleCase(v.accent) : null, v.gender].filter(Boolean).join(' ');
            return {
                id: v.id,
                label: descr ? `${v.name} — ${descr}` : v.name,
                provider: 'elevenlabs' as const,
                group: deriveVoiceGroup(v.accent, v.language, v.languages),
                public_owner_id: v.public_owner_id ?? null,
            } satisfies AgentVoiceOption;
        });
};

/**
 * Languages the agent guide can narrate in. The script (typed or AI-drafted)
 * is written in the chosen language and ElevenLabs Flash v2.5 speaks it with
 * the selected voice. Codes are ISO 639-1 (ElevenLabs `language_code`).
 * Shared with Rehub Record via @rehub/media-localisation.
 */
export type AgentLanguageOption = MediaLanguageOption;

export const AGENT_LANGUAGES: AgentLanguageOption[] = MEDIA_LANGUAGES;

/** Emoji flag for a language code (empty string if unknown). */
export const languageFlagFor = sharedLanguageFlagFor;

export const DEFAULT_LANGUAGE_CODE = SHARED_DEFAULT_LANGUAGE_CODE;

// ============================================================
// AGENT MODE 2.0 — settings resolvers & defaults
// ============================================================
// New presenter visual config (orb styling, avatar settings) and
// per-scene / per-tour overrides are stored in JSONB `meta` / `settings`
// columns. These resolvers fill sensible defaults and provide
// back-compat with the legacy `accent_color`-only voice orb.

export const DEFAULT_ORB_STYLE: OrbStyle = 'liquid_gradient';
const DEFAULT_ACCENT_COLOR = '#f97316';
const DEFAULT_ORB_SECONDARY = '#6366f1';

/** A fully-populated OrbSettings with defaults (optionally seeded by accent/style). */
export const defaultOrbSettings = (accent?: string | null, style?: OrbStyle): OrbSettings => ({
    style: style ?? DEFAULT_ORB_STYLE,
    accent_color: accent || DEFAULT_ACCENT_COLOR,
    secondary_color: DEFAULT_ORB_SECONDARY,
    size: 1,
    opacity: 1,
    position: 'bottom-right',
    intensity: 'medium',
    pulse_with_audio: true,
    idle_animation: true,
    fade: true,
});

/**
 * Resolve a presenter's orb settings, filling defaults and back-compat
 * from the legacy top-level `accent_color`. `fallbackStyle` lets the
 * viewer apply the tour's `default_orb_style` when the presenter hasn't
 * picked one explicitly.
 */
export const getOrbSettings = (
    presenter: Pick<AgentPresenter, 'accent_color' | 'meta'>,
    fallbackStyle?: OrbStyle,
): OrbSettings => {
    const raw = (presenter.meta?.orb ?? {}) as Partial<OrbSettings>;
    const base = defaultOrbSettings(presenter.accent_color, fallbackStyle);
    return {
        style: raw.style ?? base.style,
        accent_color: raw.accent_color || presenter.accent_color || base.accent_color,
        secondary_color: raw.secondary_color || base.secondary_color,
        size: typeof raw.size === 'number' ? raw.size : base.size,
        opacity: typeof raw.opacity === 'number' ? raw.opacity : base.opacity,
        position: raw.position ?? base.position,
        intensity: raw.intensity ?? base.intensity,
        pulse_with_audio: raw.pulse_with_audio ?? base.pulse_with_audio,
        idle_animation: raw.idle_animation ?? base.idle_animation,
        fade: raw.fade ?? base.fade,
    };
};

export const defaultAvatarSettings = (): AvatarSettings => ({
    source: 'generated',
    style: '',
    background: 'transparent',
    voice_id: null,
    language: null,
});

/** Resolve a presenter's AI-avatar (beta) settings with defaults. */
export const getAvatarSettings = (presenter: Pick<AgentPresenter, 'meta'>): AvatarSettings => {
    const raw = (presenter.meta?.avatar ?? {}) as Partial<AvatarSettings>;
    const base = defaultAvatarSettings();
    return {
        source: raw.source ?? base.source,
        style: raw.style ?? base.style,
        background: raw.background ?? base.background,
        voice_id: raw.voice_id ?? base.voice_id,
        language: raw.language ?? base.language,
    };
};

export const DEFAULT_RECORDED_GUIDE_DISPLAY: RecordedGuideDisplay = 'rounded_card';

export const defaultRecordedGuideSettings = (): RecordedGuideSettings => ({
    display: DEFAULT_RECORDED_GUIDE_DISPLAY,
    position: 'bottom-right',
    size: 1,
    default_media: null,
});

/** Resolve a presenter's recorded-guide (display + reusable clip) settings with defaults. */
export const getRecordedGuideSettings = (
    presenter: Pick<AgentPresenter, 'meta'>,
): RecordedGuideSettings => {
    const raw = (presenter.meta?.recorded_guide ?? {}) as Partial<RecordedGuideSettings>;
    const base = defaultRecordedGuideSettings();
    return {
        display: raw.display ?? base.display,
        position: raw.position ?? base.position,
        size: typeof raw.size === 'number' ? raw.size : base.size,
        default_media: (raw.default_media as RecordedMedia | null | undefined) ?? null,
    };
};

/**
 * Resolve the recorded clip a guide should play: the scene/tour clip
 * (`meta.recorded`) takes precedence, falling back to the presenter's
 * reusable default. Returns null when nothing has been recorded yet.
 */
export const getRecordedMedia = (
    guide: { meta?: Record<string, any> | null } | null | undefined,
    presenter?: Pick<AgentPresenter, 'meta'> | null,
): RecordedMedia | null => {
    const onGuide = (guide?.meta?.recorded ?? null) as RecordedMedia | null;
    if (onGuide && onGuide.media_url) return onGuide;
    if (presenter) {
        const fallback = getRecordedGuideSettings(presenter).default_media;
        if (fallback && fallback.media_url) return fallback;
    }
    return null;
};

/** Whether a presenter is flagged as the user's library default. */
export const isPresenterDefault = (presenter: Pick<AgentPresenter, 'meta'>): boolean =>
    presenter.meta?.is_default === true;

/** A presenter's default narration language (meta.default_language, default 'en'). */
export const getPresenterDefaultLanguage = (presenter: Pick<AgentPresenter, 'meta'>): string =>
    (presenter.meta?.default_language as string | undefined)?.toLowerCase() || DEFAULT_LANGUAGE_CODE;

/** Read per-scene overrides from `scene_guides.meta.overrides`. */
export const getSceneOverrides = (
    guide: { meta?: Record<string, any> | null } | null | undefined,
): SceneGuideOverrides => {
    const raw = (guide?.meta?.overrides ?? {}) as Partial<SceneGuideOverrides>;
    return {
        presenter_id: raw.presenter_id ?? null,
        voice_id: raw.voice_id ?? null,
        language: raw.language ?? null,
        position: raw.position,
        hidden: raw.hidden ?? false,
        marker_label: raw.marker_label ?? null,
        marker_use_name: raw.marker_use_name ?? false,
    };
};

/** Fully-populated tour-wide agent settings with defaults. */
export const defaultTourAgentSettings = (): TourAgentSettings => ({
    default_presenter_id: null,
    autoplay: false,
    show_play_button: true,
    show_captions: true,
    show_transcript: false,
    show_cta: false,
    default_language: DEFAULT_LANGUAGE_CODE,
    default_voice_id: null,
    default_orb_style: DEFAULT_ORB_STYLE,
});

/** Resolve tour-wide agent settings from `tours.settings.agent` with defaults. */
export const getTourAgentSettings = (settings: TourSettings | null | undefined): TourAgentSettings => ({
    ...defaultTourAgentSettings(),
    ...(settings?.agent ?? {}),
});

// ============================================================
// PRESENTERS
// ============================================================

export const listPresenters = async (userId: string): Promise<AgentPresenter[]> => {
    if (!isConfigured) return [];
    const { data, error } = await supabase
        .from('agent_presenters')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
    if (error) {
        console.warn('[agentGuide] listPresenters error:', error.message);
        return [];
    }
    return (data || []) as AgentPresenter[];
};

export const getPresenter = async (presenterId: string): Promise<AgentPresenter | null> => {
    if (!isConfigured) return null;
    const { data, error } = await supabase
        .from('agent_presenters')
        .select('*')
        .eq('id', presenterId)
        .single();
    if (error) {
        if (error.code !== 'PGRST116') console.warn('[agentGuide] getPresenter error:', error.message);
        return null;
    }
    return data as AgentPresenter;
};

export interface CreatePresenterInput {
    name: string;
    mode?: AgentPresenterMode;
    avatar_url?: string | null;
    loop_video_url?: string | null;
    default_voice_id?: string | null;
    default_provider?: AgentVoiceProvider;
    title?: string | null;
    accent_color?: string | null;
    meta?: Record<string, any>;
}

export const createPresenter = async (
    userId: string,
    input: CreatePresenterInput,
): Promise<AgentPresenter | null> => {
    if (!isConfigured) return null;
    const { data, error } = await supabase
        .from('agent_presenters')
        .insert({
            user_id: userId,
            name: input.name,
            mode: input.mode ?? 'image_voice',
            avatar_url: input.avatar_url ?? null,
            loop_video_url: input.loop_video_url ?? null,
            default_voice_id: input.default_voice_id ?? DEFAULT_VOICE_ID,
            default_provider: input.default_provider ?? 'elevenlabs',
            title: input.title ?? null,
            accent_color: input.accent_color ?? null,
            meta: input.meta ?? {},
        })
        .select('*')
        .single();
    if (error) {
        console.error('[agentGuide] createPresenter error:', error);
        return null;
    }
    return data as AgentPresenter;
};

export const updatePresenter = async (
    presenterId: string,
    patch: Partial<CreatePresenterInput>,
): Promise<AgentPresenter | null> => {
    if (!isConfigured) return null;
    const { data, error } = await supabase
        .from('agent_presenters')
        .update(patch)
        .eq('id', presenterId)
        .select('*')
        .single();
    if (error) {
        console.error('[agentGuide] updatePresenter error:', error);
        return null;
    }
    return data as AgentPresenter;
};

/**
 * Mark a single presenter as the user's library default, clearing the
 * `meta.is_default` flag on all of their other presenters. The default
 * presenter is what new tours pre-select for Agent Mode.
 */
export const setDefaultPresenter = async (
    userId: string,
    presenterId: string,
): Promise<boolean> => {
    if (!isConfigured) return false;
    const all = await listPresenters(userId);
    let ok = true;
    for (const p of all) {
        const shouldBeDefault = p.id === presenterId;
        if (isPresenterDefault(p) === shouldBeDefault) continue;
        const meta = { ...(p.meta ?? {}), is_default: shouldBeDefault };
        const res = await updatePresenter(p.id, { meta });
        if (!res) ok = false;
    }
    return ok;
};

export const deletePresenter = async (presenterId: string): Promise<boolean> => {
    if (!isConfigured) return false;
    const { error } = await supabase
        .from('agent_presenters')
        .delete()
        .eq('id', presenterId);
    if (error) {
        // Foreign-key violation = at least one scene_guide still references it.
        if (error.code === '23503') {
            console.warn('[agentGuide] deletePresenter blocked by FK — guide rows still reference this presenter.');
        } else {
            console.error('[agentGuide] deletePresenter error:', error);
        }
        return false;
    }
    return true;
};

// ============================================================
// SCENE GUIDES
// ============================================================

/** Bulk-load every scene_guide for a tour, joined with its presenter. */
export const listSceneGuidesForTour = async (
    tourId: string,
): Promise<ResolvedSceneGuide[]> => {
    if (!isConfigured) return [];

    // 1. Get scene IDs for this tour.
    const { data: scenes, error: sErr } = await supabase
        .from('scenes')
        .select('id')
        .eq('tour_id', tourId);
    if (sErr || !scenes?.length) return [];
    const sceneIds = (scenes as Array<{ id: string }>).map((s) => s.id);

    // 2. Load guides + presenters in two queries (cheaper than the
    //    nested join when scenes share presenters).
    const { data: guides, error: gErr } = await supabase
        .from('scene_guides')
        .select('*')
        .in('scene_id', sceneIds);
    if (gErr || !guides?.length) return [];

    const presenterIds = [...new Set((guides as SceneGuide[]).map((g) => g.presenter_id))];
    const { data: presenters, error: pErr } = await supabase
        .from('agent_presenters')
        .select('*')
        .in('id', presenterIds);
    if (pErr) return [];

    const presenterById = new Map<string, AgentPresenter>();
    for (const p of (presenters || []) as AgentPresenter[]) presenterById.set(p.id, p);

    return (guides as SceneGuide[])
        .map((g) => {
            const presenter = presenterById.get(g.presenter_id);
            if (!presenter) return null;
            return { guide: g, presenter };
        })
        .filter((x): x is ResolvedSceneGuide => x !== null);
};

export const getSceneGuide = async (sceneId: string): Promise<ResolvedSceneGuide | null> => {
    if (!isConfigured) return null;
    const { data: guide, error } = await supabase
        .from('scene_guides')
        .select('*')
        .eq('scene_id', sceneId)
        .maybeSingle();
    if (error) {
        console.warn('[agentGuide] getSceneGuide error:', error.message);
        return null;
    }
    if (!guide) return null;
    const presenter = await getPresenter((guide as SceneGuide).presenter_id);
    if (!presenter) return null;
    return { guide: guide as SceneGuide, presenter };
};

export interface UpsertSceneGuideInput {
    scene_id: string;
    presenter_id: string;
    hotspot_id?: string | null;
    script_text?: string;
    captions_enabled?: boolean;
    autoplay_on_scene_enter?: boolean;
    duck_bgm?: boolean;
    meta?: Record<string, any>;
}

/**
 * Create or update the single scene_guide row for a scene.
 * (UNIQUE constraint on scene_id means there's at most one per scene.)
 */
export const upsertSceneGuide = async (
    input: UpsertSceneGuideInput,
): Promise<SceneGuide | null> => {
    if (!isConfigured) return null;
    const payload: Record<string, any> = {
        scene_id: input.scene_id,
        presenter_id: input.presenter_id,
    };
    if (input.hotspot_id !== undefined) payload.hotspot_id = input.hotspot_id;
    if (input.script_text !== undefined) payload.script_text = input.script_text;
    if (input.captions_enabled !== undefined) payload.captions_enabled = input.captions_enabled;
    if (input.autoplay_on_scene_enter !== undefined) payload.autoplay_on_scene_enter = input.autoplay_on_scene_enter;
    if (input.duck_bgm !== undefined) payload.duck_bgm = input.duck_bgm;
    if (input.meta !== undefined) payload.meta = input.meta;

    const { data, error } = await supabase
        .from('scene_guides')
        .upsert(payload, { onConflict: 'scene_id' })
        .select('*')
        .single();
    if (error) {
        console.error('[agentGuide] upsertSceneGuide error:', error);
        return null;
    }
    return data as SceneGuide;
};

export const deleteSceneGuide = async (sceneId: string): Promise<boolean> => {
    if (!isConfigured) return false;
    const { error } = await supabase
        .from('scene_guides')
        .delete()
        .eq('scene_id', sceneId);
    if (error) {
        console.error('[agentGuide] deleteSceneGuide error:', error);
        return false;
    }
    return true;
};

// ============================================================
// TOUR GUIDES (tour-wide narration)
// ============================================================
// Mirrors the scene-guide CRUD above. UNIQUE(tour_id) on the table
// means there's at most one row per tour — upsert with onConflict
// keeps the API symmetric with `upsertSceneGuide`.

export const getTourGuide = async (tourId: string): Promise<ResolvedTourGuide | null> => {
    if (!isConfigured) return null;
    const { data: guide, error } = await supabase
        .from('tour_guides')
        .select('*')
        .eq('tour_id', tourId)
        .maybeSingle();
    if (error) {
        console.warn('[agentGuide] getTourGuide error:', error.message);
        return null;
    }
    if (!guide) return null;
    const presenter = await getPresenter((guide as TourGuide).presenter_id);
    if (!presenter) return null;
    return { guide: guide as TourGuide, presenter };
};

export interface UpsertTourGuideInput {
    tour_id: string;
    presenter_id: string;
    script_text?: string;
    captions_enabled?: boolean;
    autoplay_on_tour_enter?: boolean;
    duck_bgm?: boolean;
    meta?: Record<string, any>;
}

/**
 * Create or update the single tour_guide row for a tour. UNIQUE on
 * tour_id means a tour can only ever have one tour-wide guide active.
 */
export const upsertTourGuide = async (
    input: UpsertTourGuideInput,
): Promise<TourGuide | null> => {
    if (!isConfigured) return null;
    const payload: Record<string, any> = {
        tour_id: input.tour_id,
        presenter_id: input.presenter_id,
    };
    if (input.script_text !== undefined) payload.script_text = input.script_text;
    if (input.captions_enabled !== undefined) payload.captions_enabled = input.captions_enabled;
    if (input.autoplay_on_tour_enter !== undefined) payload.autoplay_on_tour_enter = input.autoplay_on_tour_enter;
    if (input.duck_bgm !== undefined) payload.duck_bgm = input.duck_bgm;
    if (input.meta !== undefined) payload.meta = input.meta;

    const { data, error } = await supabase
        .from('tour_guides')
        .upsert(payload, { onConflict: 'tour_id' })
        .select('*')
        .single();
    if (error) {
        console.error('[agentGuide] upsertTourGuide error:', error);
        return null;
    }
    return data as TourGuide;
};

export const deleteTourGuide = async (tourId: string): Promise<boolean> => {
    if (!isConfigured) return false;
    const { error } = await supabase
        .from('tour_guides')
        .delete()
        .eq('tour_id', tourId);
    if (error) {
        console.error('[agentGuide] deleteTourGuide error:', error);
        return false;
    }
    return true;
};

// ============================================================
// NARRATION GENERATION (edge function wrapper)
// ============================================================

export interface GenerateNarrationParams {
    scene_guide_id: string;
    script: string;
    voice_id?: string;
    /** Voice Library owner id — required on first use of a library voice. */
    public_owner_id?: string | null;
    provider?: AgentVoiceProvider;
    /** ISO 639-1 code for a translated track (stored under meta.translations). Omit for the primary language. */
    language_code?: string;
}

export interface GenerateNarrationResult {
    cached: boolean;
    audio_url: string;
    audio_hash: string;
    audio_path?: string;
    audio_provider?: AgentVoiceProvider;
    audio_voice_id?: string;
    audio_duration_ms?: number;
}

export interface PreviewNarrationResult {
    /** `audio/mpeg` data URL ready to feed straight into a transient `<audio>` element. */
    audio_data_url: string;
    provider: AgentVoiceProvider;
    voice_id: string;
}

/**
 * Invoke the `tours-generate-narration` edge function via the Supabase
 * JS SDK. We use `functions.invoke` rather than raw `fetch` because the
 * SDK injects the apikey + Authorization headers on the CORS preflight
 * OPTIONS request — without it, Supabase's platform-level JWT check
 * rejects the preflight with a 401, which the browser surfaces as a
 * CORS error (and we never reach our own auth check).
 *
 * The edge function itself still validates the user JWT independently,
 * so security is unchanged.
 */
const callNarrationFn = async <T>(body: Record<string, unknown>): Promise<T> => {
    if (!isConfigured) throw new Error('Supabase not configured');
    // Quick "not signed in" guard — invoke() would otherwise return a 401
    // wrapped in FunctionsHttpError, which is harder to message in the UI.
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session?.access_token) throw new Error('Not signed in');

    const { data, error } = await supabase.functions.invoke('tours-generate-narration', {
        body,
    });

    if (error) {
        // FunctionsHttpError surfaces the function's JSON {error,detail}
        // body on `error.context.json()` — read it if available so callers
        // see "Insufficient credits" / "ElevenLabs not configured" instead
        // of a generic "Edge Function returned a non-2xx status code".
        let detail: string | null = null;
        try {
            const ctx = (error as { context?: Response }).context;
            if (ctx && typeof ctx.json === 'function') {
                const body = await ctx.json();
                detail = body?.error || body?.detail || null;
            }
        } catch {
            // ignore — fall back to error.message
        }
        throw new Error(detail || error.message || 'tours-generate-narration failed');
    }

    if (data && typeof data === 'object' && 'error' in (data as Record<string, unknown>)) {
        throw new Error((data as { error?: string }).error || 'tours-generate-narration error');
    }

    return data as T;
};

/** Pre-render the full narration MP3 and persist on the scene_guide row. Charges 1 credit when the hash differs. */
export const generateNarration = async (
    params: GenerateNarrationParams,
): Promise<GenerateNarrationResult> => {
    return callNarrationFn<GenerateNarrationResult>({
        scope: 'scene',
        scene_guide_id: params.scene_guide_id,
        script: params.script,
        voice_id: params.voice_id,
        public_owner_id: params.public_owner_id ?? undefined,
        provider: params.provider ?? 'elevenlabs',
        language_code: params.language_code,
    });
};

export interface GenerateTourNarrationParams {
    tour_guide_id: string;
    script: string;
    voice_id?: string;
    /** Voice Library owner id — required on first use of a library voice. */
    public_owner_id?: string | null;
    provider?: AgentVoiceProvider;
    /** ISO 639-1 code for a translated track (stored under meta.translations). Omit for the primary language. */
    language_code?: string;
}

/**
 * Pre-render the full tour-wide narration MP3 and persist on the
 * tour_guide row. Same flow + credit semantics as generateNarration,
 * just routed at the edge function via `scope='tour'` so it lands
 * on `tour_guides` and uses the tour-level storage path.
 */
export const generateTourNarration = async (
    params: GenerateTourNarrationParams,
): Promise<GenerateNarrationResult> => {
    return callNarrationFn<GenerateNarrationResult>({
        scope: 'tour',
        tour_guide_id: params.tour_guide_id,
        script: params.script,
        voice_id: params.voice_id,
        public_owner_id: params.public_owner_id ?? undefined,
        provider: params.provider ?? 'elevenlabs',
        language_code: params.language_code,
    });
};

export interface GenerateHotspotNarrationParams {
    hotspot_id: string;
    script: string;
    voice_id?: string;
    /** Voice Library owner id — required on first use of a library voice. */
    public_owner_id?: string | null;
    provider?: AgentVoiceProvider;
    /** ISO 639-1 code of the track being generated. Omit for the hotspot's primary language. */
    language_code?: string;
}

/**
 * Pre-render an info-hotspot's voiceover MP3 and persist it under
 * `hotspots.meta.voice.tracks[language]`. Same 1-credit + hash-cache
 * semantics as `generateNarration`, routed at the edge function via
 * `scope='hotspot'`.
 */
export const generateHotspotNarration = async (
    params: GenerateHotspotNarrationParams,
): Promise<GenerateNarrationResult> => {
    return callNarrationFn<GenerateNarrationResult>({
        scope: 'hotspot',
        hotspot_id: params.hotspot_id,
        script: params.script,
        voice_id: params.voice_id,
        public_owner_id: params.public_owner_id ?? undefined,
        provider: params.provider ?? 'elevenlabs',
        language_code: params.language_code,
    });
};

/** Look up public_owner_id for a voice id from the current (cached) catalogue. */
export const publicOwnerIdForVoice = (
    voices: AgentVoiceOption[],
    voiceId: string | null | undefined,
): string | null => {
    if (!voiceId) return null;
    return voices.find((v) => v.id === voiceId)?.public_owner_id ?? null;
};

/** Generate a short transient preview clip for in-editor "listen before saving". No credit charge, no caching. */
export const previewNarration = async (params: {
    script: string;
    voice_id?: string;
    public_owner_id?: string | null;
    provider?: AgentVoiceProvider;
}): Promise<PreviewNarrationResult> => {
    const out = await callNarrationFn<{
        preview: true;
        provider: AgentVoiceProvider;
        voice_id: string;
        mime: string;
        audio_base64: string;
    }>({
        preview: true,
        // Scope is irrelevant for preview — the edge function never persists
        // anything; we still pass 'scene' to satisfy validation defaults.
        scope: 'scene',
        script: params.script,
        voice_id: params.voice_id,
        public_owner_id: params.public_owner_id ?? undefined,
        provider: params.provider ?? 'elevenlabs',
    });
    return {
        audio_data_url: `data:${out.mime};base64,${out.audio_base64}`,
        provider: out.provider,
        voice_id: out.voice_id,
    };
};

// ============================================================
// SCRIPT COMPOSER (AI-assisted draft via Gemini)
// ============================================================

export type ScriptTone = 'warm' | 'professional' | 'luxury' | 'friendly';
export type ScriptLength = 'short' | 'medium' | 'long';
/** Output shape: a flowing narration to read out, or bullet talking points to follow. */
export type ScriptFormat = 'script' | 'bullets';

/** Curated feature checklist shown in the ScriptComposer. Grouped for scanability. */
export interface ScriptFeatureCategory {
    id: string;
    label: string;
    features: string[];
}

export const SCRIPT_FEATURE_CATEGORIES: ScriptFeatureCategory[] = [
    {
        id: 'tour_level',
        label: 'Property highlights',
        features: [
            'Quiet street / private location',
            'Walking distance to schools',
            'Walking distance to transport',
            'Walking distance to shops / cafes',
            'Off-street parking included',
            'EV charging at the property',
            'Freehold / share of freehold',
            'Recently extended whole property',
            'Recently rewired / replumbed',
            'New roof / windows in the last 5 years',
            'Loft conversion / room in the roof',
            'Investment potential / rental yield',
            'Strong onward chain / vacant possession',
            'Energy efficient (high EPC rating)',
            'Sympathetic restoration of period features',
        ],
    },
    {
        id: 'light_space',
        label: 'Light & space',
        features: [
            'Spacious / generous proportions',
            'Bright and naturally lit',
            'South-facing',
            'Dual aspect',
            'High ceilings',
            'Open-plan layout',
            'Recently extended',
        ],
    },
    {
        id: 'finishes',
        label: 'Finishes',
        features: [
            'Recently renovated',
            'Hardwood floors',
            'Engineered oak floors',
            'New carpets',
            'Designer kitchen',
            'Marble bathroom',
            'Bespoke joinery',
            'Period features (cornicing / fireplace)',
            'Original sash windows',
        ],
    },
    {
        id: 'tech_comfort',
        label: 'Tech & comfort',
        features: [
            'Underfloor heating',
            'Air conditioning',
            'Smart home / lighting',
            'Triple glazing',
            'High-speed broadband',
            'Sound insulation',
        ],
    },
    {
        id: 'outlook',
        label: 'Outlook',
        features: [
            'Garden views',
            'City skyline view',
            'Park / green outlook',
            'Sea / water view',
            'Quiet / private aspect',
        ],
    },
    {
        id: 'storage_utility',
        label: 'Storage & utility',
        features: [
            'Plenty of storage',
            'Walk-in wardrobe',
            'Utility room',
            'En-suite bathroom',
            'Pantry / larder',
        ],
    },
    {
        id: 'outdoor',
        label: 'Outdoor',
        features: [
            'Private garden',
            'Balcony',
            'Roof terrace',
            'Off-street parking',
            'EV charging',
            'Bike storage',
        ],
    },
    {
        id: 'lifestyle',
        label: 'Lifestyle',
        features: [
            'Family-friendly layout',
            'Entertainer\u2019s kitchen / dining',
            'Work-from-home setup',
            'Pet-friendly',
            'Guest suite / annexe',
        ],
    },
];

export interface GenerateScriptParams {
    /** Detected or supplied room type (e.g. "Living Room"). Falls back to scene title. */
    room_type?: string | null;
    scene_title?: string | null;
    /** Selected feature strings from SCRIPT_FEATURE_CATEGORIES (or custom). */
    features: string[];
    tone?: ScriptTone;
    length?: ScriptLength;
    /** 'script' = flowing narration to read; 'bullets' = short talking points to follow. Defaults to 'script'. */
    format?: ScriptFormat;
    /** Free-form notes (USPs, recent renovations, etc.). */
    custom_notes?: string;
    /** Tour-level context — e.g. "3-bed flat in Marylebone". */
    property_context?: string;
    presenter_name?: string;
    /** Language to write the narration in (English name, e.g. "Spanish"). Defaults to English. */
    language?: string;
    /**
     * "Scan the room": public URL of the scene's 360° pano. The edge function
     * fetches it server-side and lets Gemini ground the script in what it
     * actually sees in the room.
     */
    scene_image_url?: string | null;
}

export interface GenerateScriptResult {
    text: string;
}

/**
 * Generate an AI-drafted narration for a scene from ticked features.
 * Free (no credits) — costs a few hundred Gemini Flash output tokens.
 */
export const generateScript = async (
    params: GenerateScriptParams,
): Promise<GenerateScriptResult> => {
    if (!isConfigured) throw new Error('Supabase not configured');
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session?.access_token) throw new Error('Not signed in');

    const { data, error } = await supabase.functions.invoke('tours-generate-script', {
        body: {
            room_type: params.room_type ?? null,
            scene_title: params.scene_title ?? null,
            features: params.features,
            tone: params.tone ?? 'warm',
            length: params.length ?? 'medium',
            format: params.format ?? 'script',
            custom_notes: params.custom_notes ?? '',
            property_context: params.property_context ?? '',
            presenter_name: params.presenter_name ?? '',
            language: params.language ?? 'English',
            scene_image_url: params.scene_image_url ?? null,
        },
    });

    if (error) {
        let detail: string | null = null;
        try {
            const ctx = (error as { context?: Response }).context;
            if (ctx && typeof ctx.json === 'function') {
                const body = await ctx.json();
                detail = body?.error || body?.detail || null;
            }
        } catch {
            // ignore
        }
        throw new Error(detail || error.message || 'tours-generate-script failed');
    }

    if (data && typeof data === 'object' && 'error' in (data as Record<string, unknown>)) {
        throw new Error((data as { error?: string }).error || 'tours-generate-script error');
    }

    const text = (data as { text?: string } | null)?.text?.trim() || '';
    if (!text) throw new Error('AI returned an empty script');
    return { text };
};

/**
 * Faithfully translate an existing narration script into another language
 * (English name, e.g. "Spanish") via tours-generate-script translate mode.
 * Free (no credits) — the credit charge happens at voiceover generation.
 */
export const translateScript = async (text: string, language: string): Promise<string> => {
    if (!isConfigured) throw new Error('Supabase not configured');
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session?.access_token) throw new Error('Not signed in');

    const { data, error } = await supabase.functions.invoke('tours-generate-script', {
        body: { translate_text: text, language },
    });
    if (error) {
        let detail: string | null = null;
        try {
            const ctx = (error as { context?: Response }).context;
            if (ctx && typeof ctx.json === 'function') {
                const body = await ctx.json();
                detail = body?.error || body?.detail || null;
            }
        } catch {
            // ignore
        }
        throw new Error(detail || error.message || 'Translation failed');
    }
    if (data && typeof data === 'object' && 'error' in (data as Record<string, unknown>)) {
        throw new Error((data as { error?: string }).error || 'Translation failed');
    }
    const out = (data as { text?: string } | null)?.text?.trim() || '';
    if (!out) throw new Error('Translation returned empty');
    return out;
};

// ============================================================
// GUIDE LANGUAGE TRACKS (read meta.translations)
// ============================================================

/** One playable narration track for a guide (primary or a translation). */
export interface GuideAudioTrack {
    language_code: string;
    label: string;
    /** Emoji flag for the track's language. */
    flag: string;
    script_text: string;
    audio_url: string | null;
}

interface GuideLikeRow {
    script_text: string;
    audio_url: string | null;
    meta?: Record<string, any> | null;
}

const languageLabelFor = (code: string): string =>
    AGENT_LANGUAGES.find((l) => l.code === code.toLowerCase())?.label ?? code.toUpperCase();

/** The guide's primary language code (meta.language_code, default 'en'). */
export const getGuidePrimaryLanguage = (g: GuideLikeRow): string =>
    (g.meta?.language_code as string | undefined)?.toLowerCase() || 'en';

/**
 * All playable tracks for a guide: the primary-language track (top-level
 * columns) plus any translation tracks in meta.translations that have audio.
 * The primary track is always first.
 */
export const getGuideLanguageTracks = (g: GuideLikeRow): GuideAudioTrack[] => {
    const primaryCode = getGuidePrimaryLanguage(g);
    const tracks: GuideAudioTrack[] = [
        { language_code: primaryCode, label: languageLabelFor(primaryCode), flag: languageFlagFor(primaryCode), script_text: g.script_text, audio_url: g.audio_url },
    ];
    const translations = (g.meta?.translations ?? {}) as Record<string, any>;
    for (const [code, t] of Object.entries(translations)) {
        if (!t || code === primaryCode) continue;
        tracks.push({
            language_code: code,
            label: languageLabelFor(code),
            flag: languageFlagFor(code),
            script_text: (t.script_text as string) || '',
            audio_url: (t.audio_url as string) || null,
        });
    }
    return tracks;
};

// ============================================================
// INFO HOTSPOT VOICE (meta.voice) — resolvers
// ============================================================

/** A fully-populated HotspotVoiceSettings with defaults (optionally seeded from stored meta). */
export const defaultHotspotVoiceSettings = (): HotspotVoiceSettings => ({
    enabled: false,
    voice_id: DEFAULT_VOICE_ID,
    provider: 'elevenlabs',
    autoplay: false,
    primary_language: DEFAULT_LANGUAGE_CODE,
    tracks: {},
});

/** Read + default-fill a hotspot's voice settings from `hotspot.meta.voice`. */
export const getHotspotVoiceSettings = (
    hotspot: Pick<Hotspot, 'meta'> | null | undefined,
): HotspotVoiceSettings => {
    const raw = (hotspot?.meta?.voice ?? {}) as Partial<HotspotVoiceSettings>;
    const base = defaultHotspotVoiceSettings();
    return {
        enabled: raw.enabled ?? base.enabled,
        voice_id: raw.voice_id ?? base.voice_id,
        provider: raw.provider ?? base.provider,
        autoplay: raw.autoplay ?? base.autoplay,
        primary_language: (raw.primary_language || base.primary_language).toLowerCase(),
        tracks: (raw.tracks as Record<string, HotspotVoiceTrack> | undefined) ?? {},
    };
};

/**
 * Playable narration tracks for an info hotspot: the primary-language
 * track first, then any other language tracks that have generated audio.
 * Used by the public viewer's "Listen" language selector.
 */
export const getHotspotVoiceLanguageTracks = (
    hotspot: Pick<Hotspot, 'meta'> | null | undefined,
): GuideAudioTrack[] => {
    const voice = getHotspotVoiceSettings(hotspot);
    const primaryCode = voice.primary_language;
    const out: GuideAudioTrack[] = [];
    const primary = voice.tracks[primaryCode];
    if (primary?.audio_url) {
        out.push({
            language_code: primaryCode,
            label: languageLabelFor(primaryCode),
            flag: languageFlagFor(primaryCode),
            script_text: primary.script_text || '',
            audio_url: primary.audio_url,
        });
    }
    for (const [code, t] of Object.entries(voice.tracks)) {
        if (code === primaryCode || !t?.audio_url) continue;
        out.push({
            language_code: code,
            label: languageLabelFor(code),
            flag: languageFlagFor(code),
            script_text: t.script_text || '',
            audio_url: t.audio_url,
        });
    }
    return out;
};

// ============================================================
// AI PRESENTER PORTRAIT GENERATION (edge function wrapper)
// ============================================================
// Generates a one-off presenter portrait via the
// `tours-generate-presenter` edge function (Gemini image model).
// Free — no credit charge. Returns a data URL the caller then
// persists to storage via `uploadPresenterAvatarFromDataUrl`.

/** Curated style presets shown as chips in the "Generate with AI" panel. */
export interface PresenterStylePreset {
    id: string;
    label: string;
    /** Short style direction appended to the generation prompt. */
    prompt: string;
}

export const PRESENTER_STYLE_PRESETS: PresenterStylePreset[] = [
    { id: 'professional', label: 'Professional / corporate', prompt: 'Smart business attire, confident and polished, corporate real-estate professional.' },
    { id: 'friendly', label: 'Friendly / approachable', prompt: 'Warm, friendly smile, smart-casual attire, approachable neighbourhood agent.' },
    { id: 'luxury', label: 'Luxury / editorial', prompt: 'Refined, editorial styling, premium tailoring, prime / luxury property specialist.' },
    { id: 'modern_casual', label: 'Modern casual', prompt: 'Relaxed modern style, open-collar shirt, contemporary boutique agency feel.' },
];

export interface GeneratePresenterImageParams {
    /** Selected style preset label/prompt direction. */
    style?: string;
    /** Optional free-text guidance from the creator. */
    prompt?: string;
    /** Optional data URL of an uploaded photo to restyle into a clean portrait. */
    image?: string;
}

/**
 * Generate (or restyle) a presenter portrait. Returns a data URL — the
 * caller persists it to storage with `uploadPresenterAvatarFromDataUrl`.
 * Free (no credits) — uses the same JWT-validated edge-function pattern
 * as `generateScript`.
 */
export const generatePresenterImage = async (
    params: GeneratePresenterImageParams,
): Promise<string> => {
    if (!isConfigured) throw new Error('Supabase not configured');
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session?.access_token) throw new Error('Not signed in');

    const { data, error } = await supabase.functions.invoke('tours-generate-presenter', {
        body: {
            style: params.style ?? '',
            prompt: params.prompt ?? '',
            image: params.image ?? undefined,
        },
    });

    if (error) {
        let detail: string | null = null;
        try {
            const ctx = (error as { context?: Response }).context;
            if (ctx && typeof ctx.json === 'function') {
                const body = await ctx.json();
                detail = body?.error || body?.detail || null;
            }
        } catch {
            // ignore — fall back to error.message
        }
        throw new Error(detail || error.message || 'tours-generate-presenter failed');
    }

    if (data && typeof data === 'object' && 'error' in (data as Record<string, unknown>)) {
        throw new Error((data as { error?: string }).error || 'tours-generate-presenter error');
    }

    const imageUrl = (data as { imageUrl?: string } | null)?.imageUrl;
    if (!imageUrl) throw new Error('AI returned no image');
    return imageUrl;
};

// ============================================================
// PRESENTER ASSET UPLOADS (avatars / loop videos)
// ============================================================

const PRESENTERS_PREFIX = 'agent-presenters';

const fileExt = (file: File, fallback: string): string => {
    const parts = file.name.split('.');
    if (parts.length > 1) return parts[parts.length - 1].toLowerCase();
    return fallback;
};

/** Decode a `data:` URL into a Blob (used to persist AI-generated portraits). */
const dataUrlToBlob = (dataUrl: string): { blob: Blob; ext: string } => {
    const m = dataUrl.match(/^data:(image\/(png|jpeg|jpg|webp));base64,(.+)$/);
    if (!m) throw new Error('Unsupported image data URL');
    const mime = m[1];
    const ext = m[2] === 'jpeg' ? 'jpg' : m[2];
    const binary = atob(m[3]);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return { blob: new Blob([bytes], { type: mime }), ext };
};

export const uploadPresenterAvatar = async (
    userId: string,
    file: File,
): Promise<string | null> => {
    if (!isConfigured) return null;
    const ext = fileExt(file, 'png');
    const path = `${PRESENTERS_PREFIX}/${userId}/avatars/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from('tours').upload(path, file, {
        contentType: file.type || 'image/png',
        cacheControl: 'public, max-age=31536000, immutable',
        upsert: false,
    });
    if (error) {
        console.error('[agentGuide] uploadPresenterAvatar error:', error);
        return null;
    }
    const { data } = supabase.storage.from('tours').getPublicUrl(path);
    return data.publicUrl;
};

/**
 * Persist an AI-generated portrait (returned as a data URL by
 * `generatePresenterImage`) to the 'tours' bucket and return its public
 * URL. Data URLs are far too large to store in `avatar_url`, so we always
 * upload before saving the presenter row.
 */
export const uploadPresenterAvatarFromDataUrl = async (
    userId: string,
    dataUrl: string,
): Promise<string | null> => {
    if (!isConfigured) return null;
    let blob: Blob;
    let ext: string;
    try {
        ({ blob, ext } = dataUrlToBlob(dataUrl));
    } catch (e) {
        console.error('[agentGuide] uploadPresenterAvatarFromDataUrl decode error:', e);
        return null;
    }
    const path = `${PRESENTERS_PREFIX}/${userId}/avatars/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from('tours').upload(path, blob, {
        contentType: blob.type || 'image/png',
        cacheControl: 'public, max-age=31536000, immutable',
        upsert: false,
    });
    if (error) {
        console.error('[agentGuide] uploadPresenterAvatarFromDataUrl error:', error);
        return null;
    }
    const { data } = supabase.storage.from('tours').getPublicUrl(path);
    return data.publicUrl;
};

export const uploadPresenterLoopVideo = async (
    userId: string,
    file: File,
): Promise<string | null> => {
    if (!isConfigured) return null;
    const ext = fileExt(file, 'mp4');
    const path = `${PRESENTERS_PREFIX}/${userId}/loops/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from('tours').upload(path, file, {
        contentType: file.type || 'video/mp4',
        cacheControl: 'public, max-age=31536000, immutable',
        upsert: false,
    });
    if (error) {
        console.error('[agentGuide] uploadPresenterLoopVideo error:', error);
        return null;
    }
    const { data } = supabase.storage.from('tours').getPublicUrl(path);
    return data.publicUrl;
};

/**
 * Upload a recorded guide clip (webcam/mic recording or an uploaded
 * video/audio file) to `agent-presenters/{userId}/recordings/…` and
 * return a fully-formed `RecordedMedia` (URL + path + type + mime +
 * duration). The caller persists this into `meta.recorded` (per scene/
 * tour) or `meta.recorded_guide.default_media` (presenter default).
 */
export const uploadPresenterRecording = async (
    userId: string,
    file: Blob,
    opts?: { mediaType?: RecordedMediaType; durationMs?: number | null; ext?: string },
): Promise<RecordedMedia | null> => {
    if (!isConfigured) return null;
    const mime = file.type || (opts?.mediaType === 'audio' ? 'audio/webm' : 'video/webm');
    const mediaType: RecordedMediaType =
        opts?.mediaType ?? (mime.startsWith('audio/') ? 'audio' : 'video');
    const ext =
        opts?.ext ||
        (file instanceof File ? fileExt(file, mediaType === 'audio' ? 'webm' : 'webm') : undefined) ||
        (mime.includes('mp4') ? 'mp4'
            : mime.includes('quicktime') ? 'mov'
            : mime.includes('mpeg') ? 'mp3'
            : mime.includes('wav') ? 'wav'
            : 'webm');
    const path = `${PRESENTERS_PREFIX}/${userId}/recordings/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from('tours').upload(path, file, {
        contentType: mime,
        cacheControl: 'public, max-age=31536000, immutable',
        upsert: false,
    });
    if (error) {
        console.error('[agentGuide] uploadPresenterRecording error:', error);
        return null;
    }
    const { data } = supabase.storage.from('tours').getPublicUrl(path);
    return {
        media_url: data.publicUrl,
        media_path: path,
        media_type: mediaType,
        mime,
        duration_ms: opts?.durationMs ?? null,
        recorded_at: new Date().toISOString(),
    };
};
